import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import MessageThread from "../components/MessageThread";
import { Building2, RefreshCw, Eye, MousePointerClick, Users, Plus, Package, Loader2, ArrowUpRight, Star, MessageSquare, BadgeCheck, User } from "lucide-react";

const TABS = [
  { key: "jobs", label: "Jobs" },
  { key: "applicants", label: "Applicants" },
  { key: "messages", label: "Messages" },
];

export default function EmployerDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("jobs");
  const [data, setData] = useState({ jobs: [], credits: 0, total_views: 0, total_applies: 0, total_applications: 0 });
  const [applicants, setApplicants] = useState([]);
  const [threads, setThreads] = useState([]);
  const [openThread, setOpenThread] = useState(null);
  const [verification, setVerification] = useState({ verified: false });
  const [fetching, setFetching] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    if (!user) return;
    setFetching(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        api.get("/employer/my-jobs"),
        api.get("/employer/applicants"),
        api.get("/messages/my"),
        api.get("/employer-profile/verification"),
      ]);
      setData(r1.data);
      setApplicants(r2.data);
      setThreads(r3.data.filter((t) => t.role === "employer"));
      setVerification(r4.data);
    } finally { setFetching(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const refresh = async (jobId) => {
    setBusy(jobId);
    try { await api.post(`/employer/refresh/${jobId}`); toast.success("Bumped to top"); await load(); }
    catch { toast.error("Could not refresh"); }
    setBusy(null);
  };

  const buyBundle = async (lookup) => {
    try {
      const res = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
  };

  const goVerified = async () => {
    try {
      const res = await api.post("/payments/checkout", { lookup_key: "employer_verified_yearly", origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
  };

  if (loading) return <div className="max-w-7xl mx-auto p-12 text-zinc-500 font-mono-alt uppercase text-sm">Loading…</div>;
  if (!user) return (
    <div className="max-w-2xl mx-auto p-12 text-center">
      <Building2 size={40} className="text-yellow-500 mx-auto mb-6" />
      <h1 className="font-display text-4xl uppercase leading-none">Sign in to <span className="text-yellow-500">manage jobs</span></h1>
    </div>
  );

  const stats = [
    { label: "Live Jobs", value: data.jobs.length, icon: <Building2 size={18} /> },
    { label: "Views", value: data.total_views, icon: <Eye size={18} /> },
    { label: "Apply Clicks", value: data.total_applies, icon: <MousePointerClick size={18} /> },
    { label: "Applications", value: data.total_applications, icon: <Users size={18} /> },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest mb-2 flex items-center gap-2">
            Employer Dashboard
            {verification.verified && (
              <span className="inline-flex items-center gap-1 border border-yellow-500 text-yellow-500 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold"><BadgeCheck size={10} /> Verified</span>
            )}
          </div>
          <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">Your <span className="text-yellow-500">listings</span></h1>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Link to="/post-job" data-testid="employer-new-job" className="bg-yellow-500 text-black font-bold uppercase tracking-wider px-5 py-3 hover:bg-yellow-400 transition flex items-center gap-2">
            <Plus size={16} /> New Job
          </Link>
          <Link to="/sponsor-state" data-testid="employer-sponsor" className="border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black uppercase font-bold tracking-wider text-sm px-5 py-3 flex items-center gap-2 transition">
            <Star size={14} /> Sponsor a State
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
        {stats.map((s) => (
          <div key={s.label} className="border border-zinc-800 bg-zinc-950/50 p-5" data-testid={`employer-stat-${s.label.toLowerCase().replace(" ", "-")}`}>
            <div className="text-yellow-500 mb-3">{s.icon}</div>
            <div className="text-xs font-mono-alt uppercase tracking-widest text-zinc-500">{s.label}</div>
            <div className="font-display text-4xl mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      {!verification.verified && (
        <div className="border border-yellow-500/40 bg-yellow-500/5 p-5 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <BadgeCheck size={28} className="text-yellow-500 shrink-0" />
            <div>
              <div className="font-display text-xl uppercase leading-none">Get Verified &mdash; <span className="text-yellow-500">$499/year</span></div>
              <div className="text-sm text-zinc-300 mt-1">Blue-check on every job card + priority placement + rich employer profile.</div>
            </div>
          </div>
          <button data-testid="employer-verify" onClick={goVerified} className="bg-yellow-500 text-black font-bold uppercase tracking-wider text-sm px-6 py-3 hover:bg-yellow-400 transition">Upgrade Now</button>
        </div>
      )}

      <div className="border border-zinc-800 bg-zinc-950/50 p-6 mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono-alt uppercase text-yellow-500 tracking-widest">
            <Package size={14} /> Job Credits
          </div>
          <div className="font-display text-3xl mt-1"><span className="text-yellow-500">{data.credits}</span> credits available</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button data-testid="buy-bundle-5" onClick={() => buyBundle("job_bundle_5")} className="border border-zinc-800 hover:border-yellow-500 text-white uppercase tracking-wider text-sm px-5 py-3 transition">5-Pack <span className="text-yellow-500">$199</span></button>
          <button data-testid="buy-bundle-10" onClick={() => buyBundle("job_bundle_10")} className="border border-yellow-500 bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-500 uppercase tracking-wider text-sm px-5 py-3 transition">10-Pack <span className="font-bold">$349</span></button>
        </div>
      </div>

      <div className="flex border border-zinc-800 mb-8">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`emp-tab-${t.key}`}
            onClick={() => { setTab(t.key); setOpenThread(null); }}
            className={`flex-1 px-5 py-3 uppercase tracking-wider text-xs font-bold transition ${tab === t.key ? "bg-yellow-500 text-black" : "text-zinc-400 hover:text-white"}`}
          >
            {t.label}
            {t.key === "applicants" && applicants.length > 0 && <span className={`ml-2 ${tab === t.key ? "text-black/70" : "text-yellow-500"}`}>{applicants.length}</span>}
            {t.key === "messages" && threads.reduce((n, x) => n + (x.unread || 0), 0) > 0 && <span className="ml-2 text-yellow-500">•</span>}
          </button>
        ))}
      </div>

      {tab === "jobs" && (fetching ? <Loading /> : data.jobs.length === 0 ? (
        <Empty msg="No jobs yet" cta="Post a Job" to="/post-job" />
      ) : (
        <div className="border border-zinc-800 overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-zinc-950">
              <tr className="text-left text-xs font-mono-alt uppercase tracking-widest text-zinc-500">
                <th className="p-4">Title</th><th className="p-4">Posted</th>
                <th className="p-4 text-right">Views</th><th className="p-4 text-right">Clicks</th>
                <th className="p-4 text-right">Applications</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {data.jobs.map((j) => (
                <tr key={j.id} className="border-t border-zinc-800 text-sm" data-testid={`employer-job-${j.id}`}>
                  <td className="p-4">
                    <Link to={`/jobs/${j.id}`} className="font-display text-xl uppercase leading-tight hover:text-yellow-500 block">{j.title}</Link>
                    <div className="text-xs text-zinc-500">{j.location}</div>
                  </td>
                  <td className="p-4 text-zinc-400 font-mono-alt text-xs">{j.posted_date}</td>
                  <td className="p-4 text-right text-white">{j.views || 0}</td>
                  <td className="p-4 text-right text-white">{j.apply_clicks || 0}</td>
                  <td className="p-4 text-right text-yellow-500 font-bold">{j.applications || 0}</td>
                  <td className="p-4 flex justify-end gap-2">
                    <button data-testid={`refresh-${j.id}`} disabled={busy === j.id} onClick={() => refresh(j.id)} className="border border-zinc-800 hover:border-yellow-500 text-zinc-400 hover:text-yellow-500 p-2 transition">
                      {busy === j.id ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    </button>
                    <Link to={`/jobs/${j.id}`} className="border border-zinc-800 hover:border-yellow-500 text-zinc-400 hover:text-yellow-500 p-2 transition"><ArrowUpRight size={14} /></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {tab === "applicants" && (fetching ? <Loading /> : applicants.length === 0 ? (
        <Empty msg="No applicants yet" cta="Browse Jobs" to="/jobs" />
      ) : (
        <div className="space-y-2">
          {applicants.map((a) => (
            <div key={a.application_id} className="border border-zinc-800 bg-zinc-950/50 p-4 flex items-start gap-4" data-testid={`applicant-${a.application_id}`}>
              {a.applicant.picture ? <img src={a.applicant.picture} alt="" className="w-12 h-12 border border-zinc-800" /> : <div className="w-12 h-12 border border-zinc-800 flex items-center justify-center"><User size={20} className="text-zinc-500" /></div>}
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-xl uppercase leading-none">{a.applicant.name || "Anonymous"}</div>
                  {a.applicant.public && (
                    <Link to={`/u/${a.applicant.user_id}`} className="text-[10px] uppercase font-bold border border-zinc-700 hover:border-yellow-500 text-zinc-500 hover:text-yellow-500 px-2 py-0.5">View profile</Link>
                  )}
                </div>
                {a.applicant.headline && <div className="text-sm text-zinc-300 mt-1">{a.applicant.headline}</div>}
                <div className="text-xs text-zinc-500 mt-1">
                  {a.applicant.location && <>{a.applicant.location} · </>}
                  RE: <Link to={`/jobs/${a.job_id}`} className="hover:text-yellow-500">{a.job_title}</Link> · Applied {new Date(a.applied_at).toLocaleDateString()}
                </div>
                {a.applicant.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {a.applicant.skills.slice(0, 6).map((s) => (<span key={s} className="text-[10px] font-mono-alt uppercase border border-zinc-800 px-2 py-0.5 text-zinc-300">{s}</span>))}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`text-[10px] uppercase font-bold tracking-widest border px-2 py-0.5 ${statusCls(a.status)}`}>{a.status}</span>
                <button
                  data-testid={`msg-applicant-${a.application_id}`}
                  onClick={() => { setTab("messages"); setOpenThread(a.application_id); }}
                  className="border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black uppercase font-bold text-[10px] tracking-widest px-3 py-2 flex items-center gap-1 transition"
                >
                  <MessageSquare size={12} /> Message {a.unread > 0 && <span className="bg-yellow-500 text-black px-1 text-[9px]">{a.unread}</span>}
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {tab === "messages" && (
        openThread ? (
          <MessageThread applicationId={openThread} onBack={() => setOpenThread(null)} />
        ) : threads.length === 0 ? (
          <Empty msg="No conversations yet" cta="View Applicants" to="#" />
        ) : (
          <div className="space-y-2">
            {threads.map((t) => (
              <button
                key={t.application_id}
                data-testid={`thread-open-${t.application_id}`}
                onClick={() => setOpenThread(t.application_id)}
                className="w-full text-left border border-zinc-800 hover:border-yellow-500 bg-zinc-950/50 p-4 flex items-center gap-4 transition"
              >
                {t.other_picture ? <img src={t.other_picture} alt="" className="w-10 h-10 border border-zinc-800" /> : <div className="w-10 h-10 border border-zinc-800 flex items-center justify-center"><User size={16} className="text-zinc-500" /></div>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display uppercase text-lg leading-none">{t.other_name}</div>
                    {t.unread > 0 && <span className="bg-yellow-500 text-black text-[10px] font-bold px-2 py-0.5">{t.unread}</span>}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">RE: {t.job_title}</div>
                  {t.last_message && <div className="text-sm text-zinc-400 mt-1 truncate">{t.last_message.body}</div>}
                </div>
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function Loading() { return <div className="text-zinc-500 font-mono-alt uppercase text-sm">Loading…</div>; }
function Empty({ msg, cta, to }) {
  return (
    <div className="border border-zinc-800 p-12 text-center">
      <div className="font-display text-3xl uppercase mb-2">{msg}</div>
      {to !== "#" && <Link to={to} className="inline-block bg-yellow-500 text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-yellow-400 transition mt-2">{cta}</Link>}
    </div>
  );
}
function statusCls(s) {
  const m = { applied: "text-blue-400 border-blue-400/40", interviewing: "text-yellow-500 border-yellow-500/40", offer: "text-green-500 border-green-500/40", rejected: "text-red-500 border-red-500/40", withdrawn: "text-zinc-500 border-zinc-500/40" };
  return m[s] || "text-zinc-500 border-zinc-700";
}
